import type { CSSProperties } from "react";
import { CTC_BANDS, PLACEMENT_YEARS } from "../../lib/placementTrends";

/**
 * FIG-PLC-002 — how the CTC mix moved, 2022 to 2026.
 *
 * The bands are ORDINAL (< 3 LPA up to 12+), so this is a sequential ramp
 * rather than seven categorical hues, and its steps come from the site's own
 * --navy-* scale via --chart-seq-*.
 *
 * Internship is deliberately NOT a segment. It is not a salary band, it was
 * 252 of the 2026 batch's 639, and carrying it inside the stack both distorted
 * a chart about pay and spent the gold that now means one thing only. It sits
 * beside each row as its own figure.
 *
 * Each row is drawn to its own band total, so 2022's four-student and 2023's
 * one-student gaps against the stated total do not skew the mix; the note
 * beneath the section states them.
 */
const ROWS = PLACEMENT_YEARS.map((y) => {
  const segs = CTC_BANDS.map((b, i) => ({
    key: b.key,
    label: b.label,
    n: y.ctc[b.key],
    c: `var(--chart-seq-${i + 1})`,
    ink: `var(--chart-ink-${i + 1})`,
  })).filter((s) => s.n > 0);
  return {
    year: y.year,
    internship: y.internship,
    total: segs.reduce((n, s) => n + s.n, 0),
    segs,
  };
});

export function PlacementCtcBands() {
  return (
    <figure className="pfig">
      <div className="pfig-head">
        <span className="pfig-meta">
          FIG-PLC-002 &middot; share of students placed by CTC band &middot; source: BIET Training
          &amp; Placement Cell
        </span>
      </div>

      <div className="pfig-rows">
        {ROWS.map((r) => (
          <div className="pfig-row" key={r.year}>
            <span className="pfig-year">{r.year}</span>
            <div className="pfig-bar">
              {r.segs.map((s) => {
                const pct = (s.n / r.total) * 100;
                return (
                  <div
                    key={s.key}
                    className="pfig-seg"
                    style={{ flex: `${pct} 1 0`, "--c": s.c, "--ink": s.ink } as CSSProperties}
                    data-tip={`${r.year} · ${s.label} · ${s.n} students (${Math.round(pct)}%)`}
                    tabIndex={0}
                  >
                    {pct > 6 ? s.n : ""}
                  </div>
                );
              })}
            </div>
            <span className="pfig-aside">
              <b>{r.internship}</b> internship
            </span>
          </div>
        ))}
      </div>

      <div className="pfig-legend">
        {CTC_BANDS.map((b, i) => (
          <span key={b.key}>
            <i
              className="pfig-key"
              style={{ "--k": `var(--chart-seq-${i + 1})` } as CSSProperties}
            />
            {b.label}
          </span>
        ))}
      </div>
    </figure>
  );
}
